<template>
  <!-- Chart container starts here -->
  <div>
    <div class="w-100" v-if="trend.length > 1">
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 100 33"
    preserveAspectRatio="none"
  >
    <g
      v-for="(stage, index) in stagesForVerticalDisplay()"
      :key="stage.name"
    >
      <rect
        :height="`${stageWidth(stage, marker) * 0.33}`"
        x="5"
        :y="stage.startingPlace * 0.33 - 3"
        width="1"
        :style="`fill: ${colorMap[stage.name]}`"
        :class="`${index === 0 ? 'rounded-left' : ''} ${
          index === marker.range.stages.length - 1
            ? 'rounded-right'
            : ''
        }`"
      ></rect>
      <rect
        :height="`${stageWidth(stage, marker) * 0.33}`"
        x="5"
        :y="stage.startingPlace * 0.33 - 3"
        width="100"
        :style="`fill: ${colorMap[stage.name]}`"
        :class="`${index === 0 ? 'rounded-left' : ''} ${
          index === marker.range.stages.length - 1
            ? 'rounded-right'
            : ''
        }`"
        class="opacity-25"
      ></rect>
      <path
        v-if="index !== stagesForVerticalDisplay().length - 1"
        :d="`M5 ${stage.startingPlace * 0.33 - 3} L100 ${
          stage.startingPlace * 0.33 - 3
        }`"
        :stroke="colorMap[stagesForVerticalDisplay()[index].name]"
        stroke-width=".1"
        class="opacity-50"
        fill="none"
      />
    </g>
    <text
      text-anchor="left"
      v-for="stage in stagesForVerticalDisplay()"
      :key="stage.name"
      :x="0"
      :y="stage.startingPlace * 0.33 - 2.5"
      class="text-sm"
      style="font-size: 1.6px"
    >
      {{ stage.highest }}
    </text>
    <path
      :d="createPathForResults()"
      class="stroke-current text-secondary opacity-50"
      stroke-width=".2"
      fill="none"
    />
    <circle
      :data-test="value.stageName"
      :fill="colorMap[value.stageName]"
      v-for="(value, index) in trend"
      :key="index"
      :cx="(index + 1) * 20"
      :cy="(100 - resultAsPecentage(value.result)) * 0.33 - 3"
      r="1"
    />
    <text
      text-anchor="middle"
      v-for="(value, index) in trend"
      :key="index"
      :x="(index + 1) * 20"
      y="33"
      class="text-sm"
      style="font-size: 1.4px"
    >
      {{
        new Date(value.date).toLocaleDateString("en-GB", {
          month: "short",
          day: "numeric",
        })
      }}
    </text>
  </svg>
</div>
    <div v-else>
      <p class="mb-2">
        Regular blood testing means you can witness the changes to
        your body that you cannot physically see.
      </p>

      <p class="mb-2">
        Whilst you may feel lighter, brighter and be sleeping better
        testing every 3 months will show you the improvements you're
        making on the inside.
      </p>

      <p class="mb-2">
        You can also view your trends over time, the more tests you
        have the better the picture will be of your over all health
        and improvements.
      </p>
    </div>
  </div>
</template>
<style>
.stroke-current {
  stroke:currentColor;
}
</style>
<script setup>
import { defineProps } from 'vue'
import { useResultsStore } from "@/store/results";
import { RESULT_COLORS } from "@/styles/base/resultColors";

const props = defineProps({
  marker: Object,
  selectedBloodTest: Object,
});

const resultsStore = useResultsStore();

const trend = props.marker.resultRangeStage
  ? getMarkerResultsOverTime(props.marker)
  : [];

function stageWidth(stage, marker) {
  return Math.ceil(
    ((stage.highest - stage.lowest) /
      (marker.range.highestPossible - marker.range.lowestPossible)) *
    100
  );
}

function resultAsPecentage(result) {
  const value = result || props.marker.result;
  // (value - lower bound)/(upper bound - lower bound)
  return (
    ((value - props.marker.range.lowestPossible) /
      (props.marker.range.highestPossible -
        props.marker.range.lowestPossible)) *
    100
  );
}

function stagesForVerticalDisplay() {
  return props.marker.range.stages.map((stage) => {
    return {
      ...stage,
      startingPlace: 100 - resultAsPecentage(stage.highest),
    };
  });
}

function createPathForResults() {
  return trend.map((result, index) => {
    return `${index === 0 ? "M" : "L"}${(index + 1) * 20} ${(100 - resultAsPecentage(result.result)) * 0.33 - 3
      }`;
  });
}

// Same colours as the range bar on the Result tab.
const colorMap = {
  rangeLow: RESULT_COLORS.poor,
  rangeLowNormal: RESULT_COLORS.normal,
  rangeNormal: RESULT_COLORS.good,
  rangeHighNormal: RESULT_COLORS.normal,
  rangeHigh: RESULT_COLORS.poor,
};

function getMarkerResultsOverTime(targetMarker) {
  return [...resultsStore.bloodResults]
    .sort((a, b) => {
      const date1 = Date.parse(a.date);
      const date2 = Date.parse(b.date);
      if (date1 > date2) {
        return 1;
      } else if (date1 < date2) {
        return -1;
      } else {
        return 0;
      }
    })
    .flatMap(() =>
      props.selectedBloodTest.result.panels.flatMap((panel) =>
        panel.markers
          .filter((marker) => marker.name === targetMarker.name)
          .map((marker) => ({
            date: props.selectedBloodTest.result.date,
            result: marker.result,
            stageName: marker.resultRangeStage.name,
          }))
      )
    );
}
</script>
